import { AxisTickProps } from '@nivo/axes'
import { BarCustomLayerProps, BarDatum } from '@nivo/bar'
import dynamic from 'next/dynamic'
import { ChartContainer, Skeleton, Stack, StackProps } from '~/design_system'
import { SadStates } from '~/components/SadStates'
import { useChartFormat, useFormattedTitle } from './format'
import { CustomLinkChartProps, GenericChartProps, ReportKeyChartProps } from './ChartTypes'

// nivo elements have to be dynamically imported because d3 went ESM only and nextjs doesn't like it
const ResponsiveBar = dynamic(() => import('@nivo/bar').then(module => module.ResponsiveBar), { ssr: false })

const MAX_VERTICAL_LABEL_LENGTH = 18
const MAX_HORIZONTAL_LABEL_LENGTH = 8

const getBarLabelPosition = (layout: string, width: number, height: number) =>
  layout === 'horizontal'
    ? { x: width + 8, y: height / 2, textAnchor: 'start' }
    : { x: width / 2, y: -10, textAnchor: 'middle' }

const Bars =
  (layout: string) =>
  ({ bars }: BarCustomLayerProps<BarDatum>) => {
    return bars.map(({ x, y, width, height, color, data }, idx) => {
      return (
        // eslint-disable-next-line react/no-array-index-key
        <g transform={`translate(${x}, ${y})`} key={idx}>
          <rect width={width} height={height} rx="0" ry="0" fill={color} strokeWidth="0" stroke={color} />
          <text
            {...getBarLabelPosition(layout, width, height)}
            dominantBaseline="central"
            style={{ fontFamily: 'sans-serif', fontSize: '14px', pointerEvents: 'none' }}>
            {data.formattedValue}
          </text>
        </g>
      )
    })
  }

const getChartProps = (layout: string) => {
  return layout === 'horizontal'
    ? {
        colors: '#26b999',
        enableGridX: true,
        enableGridY: false,
        gridXValues: [0],
        margin: { bottom: 24, left: 160, right: 64, top: 24 }
      }
    : {
        colors: '#3077cc',
        enableGridX: false,
        enableGridY: true,
        gridYValues: [0],
        margin: { bottom: 48, left: 12, right: 12, top: 24 },
        xAxisLabelOffset: 40,
        yAxisLabelOffset: -60
      }
}

const getDefaultSubtitle = (count = 0) => {
  const startsWith = count >= 10 ? 'Top ' : ''
  const endsWith = count > 1 ? 'Results' : 'Result'
  return `${startsWith}${count} ${endsWith}`
}

interface BaseBarChartProps extends GenericChartProps {}

interface CustomLinkBarChartProps extends BaseBarChartProps, CustomLinkChartProps {}

interface ReportKeyBarChartProps extends BaseBarChartProps, ReportKeyChartProps {}

export type BarChartProps = CustomLinkBarChartProps | ReportKeyBarChartProps

export const BarChart = ({
  data,
  chartConfig,
  reportKey,
  customReportUrl,
  isLoading = true,
  isFetching = true,
  isError = false,
  isEmpty = false
}: BarChartProps) => {
  const {
    format = 'number',
    xAxisLabel,
    yAxisLabel,
    title = `${xAxisLabel} By ${yAxisLabel}`,
    layout = 'horizontal',
    subTitle,
    xAxisFormat = (label: string) => label,
    yAxisFormat = (label: string) => label
  } = chartConfig

  const props = getChartProps(layout)
  const chartFormat = useChartFormat('number')
  // Nivo must be hijacking the data prop because the .reverse method is not working
  //   any longer but by cloning the data prop, it works again
  const clonedData = JSON.parse(JSON.stringify(data || []))

  return (
    <ChartContainer
      title={useFormattedTitle(title, format)}
      subTitle={subTitle || (layout === 'horizontal' ? getDefaultSubtitle(data?.length) : '')}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      isEmpty={isEmpty}
      reportKey={reportKey}
      customReportUrl={customReportUrl}>
      <SadStates
        states={[
          {
            when: isLoading,
            render: () => (layout === 'horizontal' ? <HorizontalBarChartSkeleton /> : <VerticalBarChartSkeleton />)
          }
        ]}>
        {data && (
          <ResponsiveBar
            data={layout === 'horizontal' ? clonedData?.reverse() : clonedData}
            layout={layout}
            {...props}
            valueFormat={chartFormat}
            layers={['grid', 'axes', Bars(layout), 'markers', 'legends', 'annotations']}
            padding={0.4}
            axisBottom={{
              tickSize: 0,
              tickValues: 0,
              format: xAxisFormat,
              renderTick: props => customTick(props, 'horizontal')
            }}
            axisLeft={
              layout === 'horizontal'
                ? {
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: 0,
                    format: yAxisFormat,
                    renderTick: props => customTick(props, 'vertical')
                  }
                : null
            }
          />
        )}
      </SadStates>
    </ChartContainer>
  )
}

function customTick(
  { opacity, textAnchor, textBaseline, textX, textY, value, x, y, format }: AxisTickProps<any>,
  orientation: string
) {
  const formattedValue = format ? format(value) : value
  const maxLabelLength = orientation === 'vertical' ? MAX_VERTICAL_LABEL_LENGTH : MAX_HORIZONTAL_LABEL_LENGTH
  const truncatedValue =
    formattedValue.length > maxLabelLength ? `${formattedValue.substr(0, maxLabelLength)}…` : formattedValue
  return (
    <g transform={`translate(${x},${y})`} style={{ opacity }}>
      <text
        fontSize="14px"
        dominantBaseline={textBaseline}
        textAnchor={textAnchor}
        transform={`translate(${textX},${textY})`}>
        <title>{formattedValue}</title>
        {truncatedValue}
      </text>
    </g>
  )
}

const HorizontalBarChartSkeleton = (props: StackProps) => {
  const widths = [96, 64, 48, 40, 32, 24, 18, 6]
  return (
    <Stack p={8} spacing={3} width="full" {...props}>
      {widths.map((width, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Skeleton h="25px" width={`${width}%`} key={idx} />
      ))}
    </Stack>
  )
}

const VerticalBarChartSkeleton = (props: StackProps) => {
  const hideOnMobile = { display: { base: 'none', sm: 'block' } }

  return (
    <Stack
      p={8}
      spacing={5}
      direction="row"
      height="full"
      width={{ base: '80%', sm: '70%' }}
      mx="auto"
      alignItems="flex-end"
      {...props}>
      <Skeleton w="35px" height="18%" />
      <Skeleton w="35px" height="24%" {...hideOnMobile} />
      <Skeleton w="35px" height="32%" />
      <Skeleton w="35px" height="40%" {...hideOnMobile} />
      <Skeleton w="35px" height="48%" {...hideOnMobile} />
      <Skeleton w="35px" height="64%" />
      <Skeleton w="35px" height="96%" />
    </Stack>
  )
}
